import { useEffect, useState } from 'react';
import axios from 'axios';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const profileSchema = z.object({
  email: z.string().email('Invalid email address'),
  phone: z.string().min(10, 'Phone number must be at least 10 digits'),
  password: z.string().optional(),
});

export default function AdminProfile() {
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(profileSchema),
  });

  useEffect(() => {
    fetchProfile();
  }, []);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('adminToken');
    return { headers: { Authorization: `Bearer ${token}` } };
  };

  const fetchProfile = async () => {
    try {
      const response = await axios.get(`${API}/admin/profile`, getAuthHeaders());
      setValue('email', response.data.email);
      setValue('phone', response.data.phone);
    } catch (error) {
      console.error('Error fetching profile:', error);
      toast.error('Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = async (data) => {
    setIsSubmitting(true);
    const updateData = {
      email: data.email,
      phone: data.phone,
    };
    if (data.password) {
      updateData.password = data.password;
    }

    try {
      await axios.put(`${API}/admin/profile`, updateData, getAuthHeaders());
      toast.success('Profile updated successfully');
    } catch (error) {
      console.error('Error updating profile:', error);
      toast.error(error.response?.data?.detail || 'Failed to update profile');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl p-6 shadow-sm text-center py-12">
        <Loader2 className="inline-block h-8 w-8 animate-spin text-brand-blue" />
        <p className="mt-4 text-slate-600">Loading profile...</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl p-6 shadow-sm">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-brand-dark font-display">Admin Profile</h1>
        <p className="text-slate-600 mt-2">Update your contact information and password</p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} data-testid="profile-form" className="max-w-xl space-y-6">
        <div>
          <Label htmlFor="email">Email *</Label>
          <Input
            id="email"
            type="email"
            data-testid="profile-email-input"
            {...register('email')}
            placeholder="admin@tutorflex.com"
            className="mt-2"
          />
          {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email.message}</p>}
        </div>

        <div>
          <Label htmlFor="phone">Phone *</Label>
          <Input
            id="phone"
            data-testid="profile-phone-input"
            {...register('phone')}
            placeholder="+91 1234567890"
            className="mt-2"
          />
          {errors.phone && <p className="text-red-500 text-sm mt-1">{errors.phone.message}</p>}
        </div>

        <div>
          <Label htmlFor="password">New Password (Optional)</Label>
          <Input
            id="password"
            type="password"
            data-testid="profile-password-input"
            {...register('password')}
            placeholder="Leave blank to keep current password"
            className="mt-2"
          />
          {errors.password && <p className="text-red-500 text-sm mt-1">{errors.password.message}</p>}
        </div>

        <Button
          type="submit"
          data-testid="profile-submit-btn"
          disabled={isSubmitting}
          className="bg-brand-blue hover:bg-blue-600"
        >
          {isSubmitting ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Updating...
            </>
          ) : (
            'Update Profile'
          )}
        </Button>
      </form>
    </div>
  );
}
